import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHistory, 
  faFileExcel, 
  faCheckCircle, 
  faExclamationTriangle,
  faSpinner,
  faDownload,
  faEye,
  faRedo,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import { format } from 'date-fns';

interface ImportRecord {
  id: string;
  filename: string;
  source_system: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  total_records: number;
  leads_created: number;
  failed_records: number;
  created_at: string;
  completed_at?: string;
  processing_time?: string;
  error_message?: string;
}

interface ImportHistoryProps {
  refreshTrigger?: number;
}

const ImportHistory: React.FC<ImportHistoryProps> = ({ refreshTrigger }) => {
  const [imports, setImports] = useState<ImportRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedImport, setSelectedImport] = useState<ImportRecord | null>(null);

  useEffect(() => {
    fetchImportHistory();
  }, [refreshTrigger]);

  const fetchImportHistory = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const response = await axiosInstance.get('/contact-import/history');
      
      if (response.data.success) {
        setImports(response.data.imports || []);
      } else {
        setError('Failed to load import history');
      }
    } catch (err: any) {
      console.error('Import history error:', err);
      setError(err.response?.data?.message || 'Failed to load import history');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge bg="success"><FontAwesomeIcon icon={faCheckCircle} className="me-1" />Completed</Badge>;
      case 'processing':
        return <Badge bg="primary"><FontAwesomeIcon icon={faSpinner} spin className="me-1" />Processing</Badge>;
      case 'queued':
        return <Badge bg="info"><FontAwesomeIcon icon={faClock} className="me-1" />Queued</Badge>;
      case 'failed':
        return <Badge bg="danger"><FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />Failed</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  const downloadErrorReport = async (importId: string) => {
    try {
      const response = await axiosInstance.get(`/contact-import/errors/${importId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `import-errors-${importId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  if (isLoading) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading import history...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <Alert variant="danger">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {error}
            <Button 
              variant="outline-danger" 
              size="sm" 
              className="ms-3"
              onClick={fetchImportHistory}
            >
              <FontAwesomeIcon icon={faRedo} className="me-1" />
              Retry
            </Button>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-gradient-primary text-white">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <FontAwesomeIcon icon={faHistory} className="me-2" />
            <span>Import History</span>
          </div>
          <Button 
            variant="light" 
            size="sm"
            onClick={fetchImportHistory}
          >
            <FontAwesomeIcon icon={faRedo} className="me-1" />
            Refresh
          </Button>
        </div>
      </Card.Header>
      
      <Card.Body>
        {imports.length === 0 ? (
          <Alert variant="info" className="mb-0">
            <FontAwesomeIcon icon={faFileExcel} className="me-2" />
            No imports found. Upload a file to get started!
          </Alert>
        ) : (
          <>
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>File</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th className="text-center">Records</th>
                    <th className="text-center">Leads Created</th>
                    <th className="text-center">Failed</th>
                    <th>Processing Time</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {imports.map((importRecord, index) => (
                    <tr key={`import-${importRecord.id}-${index}`}>
                      <td className="small">{formatDate(importRecord.created_at)}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faFileExcel} className="text-success me-2" />
                          <span className="text-truncate" style={{ maxWidth: '200px' }}>
                            {importRecord.filename}
                          </span>
                        </div>
                      </td>
                      <td>{importRecord.source_system}</td>
                      <td>{getStatusBadge(importRecord.status)}</td>
                      <td className="text-center">{importRecord.total_records.toLocaleString()}</td>
                      <td className="text-center text-success">
                        <strong>{importRecord.leads_created.toLocaleString()}</strong>
                      </td>
                      <td className="text-center">
                        {importRecord.failed_records > 0 ? (
                          <span className="text-danger">{importRecord.failed_records}</span>
                        ) : (
                          <span className="text-muted">0</span>
                        )}
                      </td>
                      <td className="small">{importRecord.processing_time || '-'}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setSelectedImport(importRecord)}
                            title="View Details"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                          {importRecord.failed_records > 0 && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => downloadErrorReport(importRecord.id)}
                              title="Download Error Report"
                            >
                              <FontAwesomeIcon icon={faDownload} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Summary Stats */}
            <Row className="mt-4 text-center">
              <Col md={3}>
                <Card className="border-0 bg-light">
                  <Card.Body className="py-3">
                    <h4 className="mb-0 text-primary">{imports.length}</h4>
                    <small className="text-muted">Total Imports</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 bg-light">
                  <Card.Body className="py-3">
                    <h4 className="mb-0 text-success">
                      {imports.reduce((sum, imp) => sum + imp.leads_created, 0).toLocaleString()}
                    </h4>
                    <small className="text-muted">Total Leads Created</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 bg-light">
                  <Card.Body className="py-3">
                    <h4 className="mb-0 text-info">
                      {imports.filter(imp => imp.status === 'completed').length}
                    </h4>
                    <small className="text-muted">Successful Imports</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 bg-light">
                  <Card.Body className="py-3">
                    <h4 className="mb-0 text-warning">
                      {imports.reduce((sum, imp) => sum + imp.failed_records, 0).toLocaleString()}
                    </h4>
                    <small className="text-muted">Total Failed Records</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ImportHistory;